import { useMutation } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { EditLayer, Field, LayerButtons, Notice, ReadonlyValue, Switch } from '@/admin/AdminShell'
import type { PickingStrategy, WarehouseList, ZoneInput, ZoneRow } from './topologyApi'
import { createZone, pickCounterparties, updateZone } from './topologyApi'
import { NamePicker } from './NamePicker'
import { CODE } from './WarehousesPage'

/**
 * Панель зоны склада: режим хранения, температура, стратегия, приоритет, владелец.
 *
 * Владелец (клиент ответственного хранения) — поле только у режима `custody`, и там оно
 * обязательно: товар в такой зоне принадлежит клиенту, и без владельца его некому выставить
 * счёт за хранение. У других режимов поля нет вовсе — сервер всё равно отверг бы владельца.
 *
 * Пустые границы температуры означают «типовые для режима» (холодильник 0…+6), а не «без
 * ограничений»: при смене режима экран подставляет типовые значения, чтобы их было видно.
 */
export interface ZoneDraft {
  id: string | null
  version?: number
  code: string
  name: string
  storageMode: string
  temperatureRegime: string
  temperatureMinC: string
  temperatureMaxC: string
  pickingStrategy: string
  priority: string
  owner: { id: string; label: string } | null
  active: boolean
}

export function newZoneDraft(zones: ZoneRow[]): ZoneDraft {
  const next = zones.reduce((max, zone) => Math.max(max, zone.priority), 0) + (zones.length ? 10 : 0)
  return {
    id: null, code: '', name: '', storageMode: 'addressed', temperatureRegime: 'ambient',
    temperatureMinC: '', temperatureMaxC: '', pickingStrategy: '', priority: String(next), owner: null, active: true,
  }
}

export function zoneDraft(zone: ZoneRow): ZoneDraft {
  return {
    id: zone.id, version: zone.version, code: zone.code, name: zone.name, storageMode: zone.storageMode,
    temperatureRegime: zone.temperatureRegime,
    temperatureMinC: zone.temperatureMinC === undefined ? '' : String(zone.temperatureMinC),
    temperatureMaxC: zone.temperatureMaxC === undefined ? '' : String(zone.temperatureMaxC),
    pickingStrategy: zone.pickingStrategy ?? '', priority: String(zone.priority),
    owner: zone.ownerCounterpartyId ? { id: zone.ownerCounterpartyId, label: zone.ownerName ?? zone.ownerCounterpartyId } : null,
    active: zone.active,
  }
}

const number = (value: string) => (value.trim() === '' ? null : Number(value.replace(',', '.')))

export function ZoneSheet({ warehouseId, draft, dictionaries, warehouseStrategy, onChange, onClose, onSaved }: {
  warehouseId: string
  draft: ZoneDraft
  dictionaries: WarehouseList
  warehouseStrategy: PickingStrategy
  onChange: (draft: ZoneDraft) => void
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useApp()
  const formId = useId()
  const [error, setError] = useState<string | null>(null)
  const creating = draft.id == null
  const custody = draft.storageMode === 'custody'
  const min = number(draft.temperatureMinC)
  const max = number(draft.temperatureMaxC)
  const rangeError = (min !== null && Number.isNaN(min)) || (max !== null && Number.isNaN(max))
    ? t('topo.zone.tempNumber')
    : min !== null && max !== null && min > max ? t('topo.zone.tempInverted') : null
  const priority = Number(draft.priority)
  const codeError = creating && draft.code !== '' && !CODE.test(draft.code) ? t('topo.codeInvalid') : null
  const incomplete = !CODE.test(draft.code) || draft.name.trim() === '' || rangeError != null
    || !Number.isInteger(priority) || (custody && draft.owner == null)

  /** Смена режима температуры подставляет типовые границы — их видно и можно поправить. */
  const changeRegime = (code: string) => {
    const regime = dictionaries.temperatureRegimes.find((r) => r.code === code)
    onChange({
      ...draft,
      temperatureRegime: code,
      temperatureMinC: regime?.defaultMinC === undefined ? '' : String(regime.defaultMinC),
      temperatureMaxC: regime?.defaultMaxC === undefined ? '' : String(regime.defaultMaxC),
    })
  }

  const save = useMutation({
    mutationFn: () => {
      const body: ZoneInput = {
        code: draft.code.trim(),
        name: draft.name.trim(),
        storageMode: draft.storageMode,
        temperatureRegime: draft.temperatureRegime,
        temperatureMinC: min,
        temperatureMaxC: max,
        pickingStrategy: draft.pickingStrategy,
        priority,
        // Владелец уходит только у ответственного хранения: у других режимов его быть не может.
        ownerCounterpartyId: custody ? (draft.owner?.id ?? null) : null,
        active: draft.active,
        version: draft.version,
      }
      return draft.id ? updateZone(draft.id, body) : createZone(warehouseId, body)
    },
    onSuccess: () => {
      onSaved()
      onClose()
    },
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })

  return (
    <EditLayer
      kind="sheet"
      overline={t('topo.zone.form')}
      title={creating ? t('topo.zone.new') : `${draft.code} — ${draft.name}`}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={creating ? t('topo.zone.add') : t('admin.save')}
                            pending={save.isPending} disabled={incomplete} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('topo.zone.form')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        <div className="form-grid">
          {creating ? (
            <Field label={t('topo.zone.code')} htmlFor="zone-code" required error={codeError} help={t('topo.code.help')}>
              <input id="zone-code" className={codeError ? 'input mono is-invalid' : 'input mono'} aria-invalid={Boolean(codeError)}
                     maxLength={32} value={draft.code} onChange={(e) => onChange({ ...draft, code: e.target.value.toUpperCase() })} />
            </Field>
          ) : (
            <Field label={t('topo.zone.code')} help={t('topo.code.locked')}>
              <ReadonlyValue mono>{draft.code}</ReadonlyValue>
            </Field>
          )}
          <Field label={t('topo.zone.name')} htmlFor="zone-name" required>
            <input id="zone-name" className="input" maxLength={255} value={draft.name} onChange={(e) => onChange({ ...draft, name: e.target.value })} />
          </Field>
        </div>
        <Field label={t('topo.zone.mode')} htmlFor="zone-mode" help={t(`topo.mode.${draft.storageMode}.help`)}>
          <select id="zone-mode" className="input" value={draft.storageMode}
                  onChange={(e) => onChange({ ...draft, storageMode: e.target.value, owner: e.target.value === 'custody' ? draft.owner : null })}>
            {dictionaries.storageModes.map((mode) => <option key={mode.code} value={mode.code}>{t(`topo.mode.${mode.code}`)}</option>)}
          </select>
        </Field>
        {custody && (
          <Field label={t('topo.zone.owner')} htmlFor="zone-owner" required help={t('topo.zone.owner.help')}>
            <NamePicker id="zone-owner" kind="counterparty" search={pickCounterparties}
                        value={draft.owner} onChange={(owner) => onChange({ ...draft, owner })} />
          </Field>
        )}
        <Field label={t('topo.zone.regime')} htmlFor="zone-regime">
          <select id="zone-regime" className="input" value={draft.temperatureRegime} onChange={(e) => changeRegime(e.target.value)}>
            {dictionaries.temperatureRegimes.map((regime) => <option key={regime.code} value={regime.code}>{t(`topo.regime.${regime.code}`)}</option>)}
          </select>
        </Field>
        <div className="form-grid">
          <Field label={t('topo.zone.tempMin')} htmlFor="zone-tmin" error={rangeError}>
            <input id="zone-tmin" className="input mono" inputMode="decimal" value={draft.temperatureMinC}
                   onChange={(e) => onChange({ ...draft, temperatureMinC: e.target.value })} />
          </Field>
          <Field label={t('topo.zone.tempMax')} htmlFor="zone-tmax" help={t('topo.zone.temp.help')}>
            <input id="zone-tmax" className="input mono" inputMode="decimal" value={draft.temperatureMaxC}
                   onChange={(e) => onChange({ ...draft, temperatureMaxC: e.target.value })} />
          </Field>
        </div>
        <div className="form-grid">
          <Field label={t('topo.zone.strategy')} htmlFor="zone-strategy">
            <select id="zone-strategy" className="input" value={draft.pickingStrategy}
                    onChange={(e) => onChange({ ...draft, pickingStrategy: e.target.value })}>
              <option value="">{t('topo.zone.inherit', { strategy: warehouseStrategy })}</option>
              {dictionaries.strategies.map((s) => <option key={s} value={s}>{t(`topo.strategy.${s}`)}</option>)}
            </select>
          </Field>
          <Field label={t('topo.zone.priority')} htmlFor="zone-priority" help={t('topo.zone.priority.help')}>
            <input id="zone-priority" className="input mono" inputMode="numeric" value={draft.priority}
                   onChange={(e) => onChange({ ...draft, priority: e.target.value })} />
          </Field>
        </div>
        <Field label={t('topo.zone.active')} help={t('topo.zone.active.help')}>
          <Switch checked={draft.active} ariaLabel={t('topo.zone.active')} onChange={(value) => onChange({ ...draft, active: value })} />
        </Field>
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}
